import Button from '@/common/components/Button';
import Modal from '@/common/components/Modal';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EquipmentCard, EquipmentData } from '../types/equipments.type';
import { useCardManuallyActivate } from '../hooks/useCardManuallyActivate';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import { cardActivationModeType } from '@/common/types/companies/companies.type';
import { zodResolver } from '@hookform/resolvers/zod';
import useBlobs from '@/common/hooks/queries/useBlobs';
import { useForm } from 'react-hook-form';
import TextArea from '@/common/components/TextArea';
import { FileUploader } from '@/common/components/FileUploader';
import { ErrorMessage } from '@/common/components/ErrorMessage';
import { CardManuallyActiveValidationSchema, cardManuallyActiveSchema } from '../schemas/cardManuallyActivate.schema';

interface CardManuallyActivateModalProps {
  equipment: EquipmentData | undefined;
  card: EquipmentCard | undefined;
  showTrigger?: boolean;
  cardManuallyActivateMode?: cardActivationModeType
}

const CardManuallyActivateModal = ({
  equipment,
  card,
  showTrigger,
  cardManuallyActivateMode
}: CardManuallyActivateModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const queryClient = useQueryClient();
  const uploadReasonRef = useRef(null);

  const isActivateModeWithReason = cardManuallyActivateMode === 'manual_with_reason'

  const {
    mutateAsync: uploadBlob,
    isPending: uploadBlobIsPending,
  } = useBlobs();

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    setError,
    reset,
    formState: { errors }
  } = useForm<CardManuallyActiveValidationSchema>({
    resolver: isActivateModeWithReason ? zodResolver(cardManuallyActiveSchema) : undefined
  })

  const file = watch('manually_activated_reason_file.file');

  const {
    mutate: cardManuallyActivate,
    isPending: cardManuallyActivateIsPending,
    isError: cardManuallyActivateIsError
  } = useCardManuallyActivate({
    id: card?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', equipment?.id] })
      setIsOpen(false)
      setMessages(['Cartão ativado com sucesso!'])
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        setMessages(error.response?.data.errors.base)
      }
    },
  });

  useEffect(reset, [isOpen])

  const onSubmit = (data: CardManuallyActiveValidationSchema) => cardManuallyActivate(data)

  const renderModalReasonBody = useCallback(() => {
    if(!isActivateModeWithReason) return undefined 

    return (
      <form className="flex flex-col gap-4">
        <TextArea
          label="Justificativa"
          className="text-white-800"
          placeholder="Escreva aqui sua justificativa"
          {...register('manually_activated_reason')}
          errorMessage={errors.manually_activated_reason?.message as string || undefined}
          markAsRequired
          maxLength={1024}
        />

        <div>
          <FileUploader
            ref={uploadReasonRef}
            name="file"
            accept="application/pdf, image/png, image/jpg, image/jpeg"
            title="Anexe o documento"
            className="w-full bg-yellow-op-2 border-none text-white-400"
            fileName={file ? file.name : undefined}
            currentFileUrl={file ? URL.createObjectURL(file) : undefined}
            handleChange={(file, blobId) => {
              clearErrors('manually_activated_reason_file');
              setValue('manually_activated_reason_file.file', file);
              setValue('manually_activated_reason_file.blob_id', blobId);
            }}
            setError={(message) => setError('manually_activated_reason_file', { message })}
            uploadBlob={uploadBlob}
            disabled={uploadBlobIsPending}
            isLoading={uploadBlobIsPending}
          />

          {errors.manually_activated_reason_file?.message && (
            <ErrorMessage
              name="file"
              message={errors.manually_activated_reason_file?.message}
            />
          )}
        </div>
      </form>
    )
  }, [])

  return (
    <>
      {showTrigger && (
        <Modal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          Trigger={(
            <Button
              disabled={cardManuallyActivateIsPending}
              operationKlassToCheckPermission="Client::Cards::ManuallyActivate"
            >
              Ativar cartão
            </Button>
          )}
          showClose={false}
          useCancellableFooter
          title="Tem certeza que deseja ativar este cartão?"
          subtitle="Essa ação não poderá ser desfeita."
          subtitleClassName="mt-1 text-md text-white-300"
          cancelButtonClassName="bg-white-100 rounded-es-lg"
          confirmButtonClassName="bg-blue-700 rounded-ee-lg"
          onConfirm={handleSubmit((data) => {
            onSubmit(data);
            setIsOpen(false);
          })}
          disabled={cardManuallyActivateIsPending}
          confirmButtonIsLoading={cardManuallyActivateIsPending}
          body={renderModalReasonBody()}
        />
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={cardManuallyActivateIsError}
        />
      )}
    </>
  )
}

export default CardManuallyActivateModal;
